import type { ReactNode, RefObject } from "react"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type {
  DataGridProps,
  GridColDef,
  GridLocaleText,
  GridPaginationModel,
  GridRowId,
  GridRowIdGetter,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid"
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridOverlay,
  gridPageCountSelector,
  gridPaginationModelSelector,
  useGridApiRef,
} from "@mui/x-data-grid"
import { enUS, esES } from "@mui/x-data-grid/locales"

import type { SxProps, Theme } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { ROWS_PER_PAGE_OPTIONS } from "helpers/utils/constants"
import { StyledDataGrid } from "widgets/styled/widgets"
import DataGridToolbar from "widgets/common/DataGridToolbar"
import TruncatedText from "widgets/common/TruncatedText"

const GRID_LOCALES = { en: enUS, es: esES }

// Every row of a table is the same height, whatever its content: one line, room for a chip or a
// small icon button, or two for the cells that stack a name over its id.
const ROW_HEIGHTS = { 1: 32, 2: 44 }

declare module "@mui/x-data-grid" {
  interface NoRowsOverlayPropsOverrides {
    emptyContent?: ReactNode
  }
  interface NoResultsOverlayPropsOverrides {
    emptyContent?: ReactNode
  }
}

export interface DataGridCursorPagination {
  hasMore: boolean
  loading: boolean
  onNextPage: () => void
}

const EmptyOverlay = ({ emptyContent }: { emptyContent?: ReactNode }) => (
  <GridOverlay>{emptyContent}</GridOverlay>
)

// 20px plus a 2px margin fills the arrow's 24px box, so nothing else in the footer moves.
const NextPageSpinner = () => <CircularProgress size={20} sx={{ m: "2px" }} />

// Plain text is cut with an ellipsis rather than wrapped, so it cannot make its row taller.
const withTruncatedText = (column: GridColDef): GridColDef => ({
  ...column,
  renderCell: (params) => {
    const content = column.renderCell ? column.renderCell(params) : params.formattedValue
    return typeof content === "string" || typeof content === "number" ? (
      <TruncatedText>{content}</TruncatedText>
    ) : (
      content
    )
  },
})

interface DataGridTableProps<R extends GridValidRowModel> {
  rows: R[]
  columns: GridColDef<R>[]
  getRowId?: GridRowIdGetter<R>
  title?: string
  // Lines of text each row has room for.
  rowLines?: keyof typeof ROW_HEIGHTS
  // Initial sort and page size; the callbacks let a table persist the user's changes.
  sortModel?: GridSortModel
  onSortModelChange?: (sortModel: GridSortModel) => void
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
  // A controlled page, for a table that keeps one per view (the devices' installation tabs).
  page?: number
  onPageChange?: (page: number) => void
  // Checkbox selection. The ids can be controlled, to clear them once an action is done.
  checkboxSelection?: boolean
  // 60px is mui-datatables' checkbox cell; a table that drops the cell's left padding narrows it.
  checkboxColumnWidth?: number
  selectedIds?: GridRowId[]
  onSelectionChange?: (ids: GridRowId[]) => void
  // Shown in the bar above the toolbar while rows are selected, as mui-datatables did.
  selectionActions?: ReactNode
  // Formatted by `formatFilename`, extension included; the grid appends ".csv" itself.
  csvFileName?: string
  // Columns the CSV carries, hidden ones included; every visible column when omitted.
  csvFields?: string[]
  toolbarActions?: ReactNode
  toolbarActionsPosition?: "start" | "end"
  disableExport?: boolean
  // Takes over the download button, for a table that only holds part of its data.
  onExportCsv?: () => void
  disableSearch?: boolean
  hideToolbar?: boolean
  hideFooter?: boolean
  // For cursor-paginated endpoints: the rows are the pages loaded so far, and moving past the last
  // one asks for the next.
  cursorPagination?: DataGridCursorPagination
  // Replaces the "no data" and "no results" labels.
  emptyContent?: ReactNode
  // Extra grid props. The wrapper's own always win, and the object-valued ones it builds (slots,
  // slotProps, initialState) cannot be passed at all: they would replace the wrapper's wholesale.
  gridProps?: Omit<Partial<DataGridProps<R>>, "slots" | "slotProps" | "initialState">
  sx?: SxProps<Theme>
  // What a page change brings back into view once its top is above the screen; the grid itself
  // when omitted. A page passes its own section to keep the title and actions above it in view.
  scrollAnchorRef?: RefObject<HTMLElement | null>
}

const DataGridTable = <R extends GridValidRowModel>({
  rows,
  columns,
  getRowId,
  title,
  rowLines = 1,
  sortModel = [],
  onSortModelChange,
  pageSize = 10,
  onPageSizeChange,
  page,
  onPageChange,
  checkboxSelection = false,
  checkboxColumnWidth = 60,
  selectedIds,
  onSelectionChange,
  selectionActions,
  csvFileName,
  csvFields,
  toolbarActions,
  toolbarActionsPosition = "end",
  disableExport = false,
  onExportCsv,
  disableSearch = false,
  hideToolbar = false,
  hideFooter = false,
  cursorPagination,
  emptyContent,
  gridProps = {},
  sx = {},
  scrollAnchorRef,
}: DataGridTableProps<R>) => {
  const { t, i18n } = useTranslation()

  const localeText = useMemo(() => {
    // A region-tagged language ("es-AR") still gets the Spanish grid, as elsewhere in the app.
    const locale = GRID_LOCALES[i18n.resolvedLanguage?.startsWith("es") ? "es" : "en"]
    const gridLocaleText = locale.components.MuiDataGrid.defaultProps.localeText
    return {
      ...gridLocaleText,
      // A count of -1 is a total still unknown: "of many" rather than the grid's "of more than 10".
      paginationDisplayedRows: (
        params: Parameters<GridLocaleText["paginationDisplayedRows"]>[0],
      ) =>
        params.count === -1
          ? t("table.ROWS_OF_MANY", { from: params.from, to: params.to })
          : (gridLocaleText.paginationDisplayedRows?.(params) ?? ""),
      // mui-datatables said the same whether the table was empty or the search found nothing.
      noRowsLabel: t("table.NO_MATCH"),
      noResultsLabel: t("table.NO_MATCH"),
    }
  }, [i18n.resolvedLanguage, t])

  const label = title ? t(title) : undefined
  const apiRef = useGridApiRef()

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize,
  })
  // Compared outside the state updater: updaters must stay pure, and StrictMode runs them twice.
  const lastPageSize = useRef(pageSize)
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      let { page } = model
      if (model.pageSize !== lastPageSize.current) {
        lastPageSize.current = model.pageSize
        onPageSizeChange?.(model.pageSize)
        // The grid keeps the page number, which points at other rows once the page size changes.
        page = 0
      }
      setPaginationModel({ ...model, page })
      onPageChange?.(page)
    },
    [onPageChange, onPageSizeChange],
  )

  const [internalSelectedIds, setInternalSelectedIds] = useState<GridRowId[]>([])
  const currentSelectedIds = selectedIds ?? internalSelectedIds
  const rowSelectionModel: GridRowSelectionModel = useMemo(
    () => ({ type: "include", ids: new Set(currentSelectedIds) }),
    [currentSelectedIds],
  )
  const handleRowSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      // "Select all" can arrive as an exclude model: every row but the listed ones.
      const ids =
        model.type === "include"
          ? Array.from(model.ids)
          : (apiRef.current?.getAllRowIds() ?? []).filter((id) => !model.ids.has(id))
      setInternalSelectedIds(ids)
      onSelectionChange?.(ids)
    },
    [apiRef, onSelectionChange],
  )

  // The widths come from the definitions alone, never from the content, so paging, sorting or
  // searching cannot move them: a flex column grows in proportion to its minWidth, and the grid
  // scrolls once the minWidths no longer fit. A new array makes the grid rebuild every column, so
  // it only changes along with the columns.
  const gridColumns = useMemo(() => {
    const textColumns = (columns as GridColDef[]).map((column) =>
      withTruncatedText(
        column.flex && column.minWidth ? { ...column, flex: column.minWidth } : column,
      ),
    )
    return checkboxSelection
      ? // mui-datatables' checkbox cell: 4px, then the checkbox padded 1rem on each side.
        [
          { ...GRID_CHECKBOX_SELECTION_COL_DEF, width: checkboxColumnWidth },
          ...textColumns,
        ]
      : textColumns
  }, [checkboxSelection, checkboxColumnWidth, columns])

  // The page whose rows the grid renders. The model is controlled, so the grid only takes a new
  // page from its props a render after ours, and shows its rows then.
  const [renderedPage, setRenderedPage] = useState(0)
  // A Suspense that hid the page detaches the api, and the grid remeasures before getting it back.
  // That change cannot move the page, so it is safe to skip.
  const handleStateChange = useCallback(() => {
    if (apiRef.current) {
      setRenderedPage(gridPaginationModelSelector(apiRef).page)
    }
  }, [apiRef])

  // mui-datatables' header was 24px with plain labels and 44px once they became sort buttons.
  const headerHeight = columns.some((column) => column.sortable !== false) ? 44 : 24

  // Paging from the footer of a long table would otherwise leave the new page's top rows above
  // the screen. Only the arrows do it: the grid also moves the page on its own, on a sort, a
  // search or a page size change, while the user is working elsewhere in the table. The scroll
  // waits for the new page's rows, before they are painted, so the old ones never move up first.
  const isScrollPending = useRef(false)
  useLayoutEffect(() => {
    if (!isScrollPending.current) {
      return
    }
    isScrollPending.current = false
    const anchor = scrollAnchorRef?.current ?? apiRef.current?.rootElementRef.current
    if (anchor && anchor.getBoundingClientRect().top < 0) {
      anchor.scrollIntoView({ block: "start" })
    }
  }, [apiRef, scrollAnchorRef, renderedPage])

  // The grid cannot show a page it has no rows for, so it moves there once they arrive, and only
  // then brings the table into view: until then the footer is what shows the fetch.
  const isNextPageRequested = useRef(false)
  useEffect(() => {
    if (!isNextPageRequested.current) {
      return
    }
    isNextPageRequested.current = false
    const { page } = gridPaginationModelSelector(apiRef)
    // An empty batch leaves it on the last page, now with the total.
    if (page + 1 < gridPageCountSelector(apiRef)) {
      isScrollPending.current = true
      apiRef.current?.setPage(page + 1)
    }
  }, [apiRef, rows])

  const basePaginationProps = {
    ...(cursorPagination && {
      // Clamping to the loaded rows would disable the next arrow on the last loaded page.
      ...(cursorPagination.hasMore && { count: -1 }),
      disabled: cursorPagination.loading,
      // Paging from the footer of a long table, the page's own spinner may be off the screen, so
      // the next arrow shows the fetch itself.
      ...(cursorPagination.loading && {
        material: { slots: { actions: { nextButtonIcon: NextPageSpinner } } },
      }),
    }),
    onPageChange: (_event: unknown, newPage: number) => {
      if (cursorPagination && newPage >= gridPageCountSelector(apiRef)) {
        isNextPageRequested.current = true
        cursorPagination.onNextPage()
      } else {
        isScrollPending.current = true
        apiRef.current?.setPage(newPage)
      }
    },
  }

  return (
    <StyledDataGrid
      {...(gridProps as Partial<DataGridProps>)}
      apiRef={apiRef}
      sx={sx}
      rows={rows}
      columns={gridColumns}
      getRowId={getRowId as GridRowIdGetter | undefined}
      label={label}
      localeText={localeText}
      initialState={{ sorting: { sortModel } }}
      paginationModel={{ ...paginationModel, page: page ?? paginationModel.page }}
      pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
      autoHeight
      rowHeight={ROW_HEIGHTS[rowLines]}
      columnHeaderHeight={headerHeight}
      // mui-datatables toggled between the two; the grid's default also cycles through unsorted.
      sortingOrder={["asc", "desc"]}
      onSortModelChange={onSortModelChange}
      onPaginationModelChange={handlePaginationModelChange}
      onStateChange={handleStateChange}
      showToolbar={!hideToolbar}
      hideFooter={hideFooter}
      disableColumnMenu
      disableColumnSelector
      disableColumnFilter
      disableDensitySelector
      disableRowSelectionOnClick
      checkboxSelection={checkboxSelection}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      hideFooterSelectedRowCount
      slots={{
        toolbar: DataGridToolbar,
        ...(emptyContent
          ? { noRowsOverlay: EmptyOverlay, noResultsOverlay: EmptyOverlay }
          : {}),
      }}
      slotProps={{
        toolbar: {
          title: label,
          actions: toolbarActions,
          actionsPosition: toolbarActionsPosition,
          disableExport,
          onExportCsv,
          disableSearch,
          selectedCount: currentSelectedIds.length,
          selectionActions,
          csvOptions: {
            fileName: csvFileName?.replace(/\.csv$/, ""),
            fields: csvFields,
            utf8WithBom: true,
          },
        },
        basePagination: basePaginationProps,
        // Only for the custom overlay: the grid's own spreads its props onto a div.
        ...(emptyContent
          ? { noRowsOverlay: { emptyContent }, noResultsOverlay: { emptyContent } }
          : {}),
      }}
    />
  )
}

export default DataGridTable
